import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ConfirmActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmText: string;
  confirmTone?: 'default' | 'danger';
  /** When non-null, requires the user to type this exact string to enable the confirm button. */
  typeToConfirm?: string;
  /** Optional reason field. When provided, the reason is passed to onConfirm. */
  reasonLabel?: string;
  onConfirm: (reason?: string) => void | Promise<void>;
  pending: boolean;
  errorMessage?: string | null;
}

export function ConfirmActionDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText,
  confirmTone = 'default',
  typeToConfirm,
  reasonLabel,
  onConfirm,
  pending,
  errorMessage,
}: ConfirmActionDialogProps): React.ReactElement {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const ready = !typeToConfirm || typed === typeToConfirm;
  const reasonReady = !reasonLabel || reason.trim().length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) {
          setTyped('');
          setReason('');
        }
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 pt-2">
          {typeToConfirm && (
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-text-secondary">
                Type <code className="font-mono">{typeToConfirm}</code> to confirm:
              </span>
              <input
                type="text"
                value={typed}
                onChange={e => {
                  setTyped(e.target.value);
                }}
                className="rounded-md border border-border bg-surface px-2 py-1 text-sm"
              />
            </label>
          )}
          {reasonLabel && (
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-text-secondary">{reasonLabel}</span>
              <textarea
                value={reason}
                rows={2}
                onChange={e => {
                  setReason(e.target.value);
                }}
                className="rounded-md border border-border bg-surface px-2 py-1 text-sm"
              />
            </label>
          )}
          {errorMessage && (
            <div role="alert" className="rounded-md bg-error/15 p-2 text-sm text-error">
              {errorMessage}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={(): void => {
                onOpenChange(false);
              }}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              onClick={(): void => {
                void onConfirm(reasonLabel ? reason.trim() : undefined);
              }}
              disabled={!ready || !reasonReady || pending}
              className={
                confirmTone === 'danger' ? 'bg-error text-white hover:bg-error' : undefined
              }
            >
              {pending ? 'Working…' : confirmText}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
